/** Goals (onboarding spine) + buying new buildings (empire expansion). */
import type { City, BSize } from './types';
import { GOALS } from './config';
import { Rng } from './rng';
import { pushLog, createBuilding } from './world';

const GOAL_CHECKS: Record<string, (c: City) => boolean> = {
  'first-building': (c) => c.buildings.length >= 1,
  'first-cash': (c) => c.runEarned >= 150,
  hire: (c) => c.buildings.reduce((n, b) => n + b.colonists.length, 0) >= 3,
  build: (c) => c.buildings.some((b) => b.rooms.length > 5),
  lookout: (c) => c.buildings.some((b) => b.rooms.some((r) => r.type === 'security')),
  survive: (c) => c.raidsSurvived >= 1,
  expand: (c) => c.buildings.length >= 2,
};

export function checkGoals(city: City): string[] {
  const done: string[] = [];
  for (const g of GOALS) {
    if (city.completedGoals.includes(g.id)) continue;
    const check = GOAL_CHECKS[g.id];
    if (check && check(city)) {
      city.completedGoals.push(g.id);
      if (g.reward > 0) city.cash += g.reward;
      pushLog(city, 'good', `✅ ${g.label}${g.reward ? ` (+$${g.reward})` : ''}`);
      done.push(g.id);
    }
  }
  return done;
}

export function currentGoal(city: City) {
  return GOALS.find((g) => !city.completedGoals.includes(g.id)) ?? null;
}

const SIZE_REP: Record<BSize, number> = { small: 0.2, med: 0.4, large: 0.7 };

/** Claim a plot on the city map: pay its cost, raise a tower, gain Reputation. */
export function doBuyPlot(city: City, plotId: number): boolean {
  const i = city.plots.findIndex((p) => p.id === plotId);
  if (i < 0) return false;
  const plot = city.plots[i];
  if (city.cash < plot.cost) return false;
  city.cash -= plot.cost;

  const rng = new Rng(city.rngSeed);
  const b = createBuilding(rng, city.nextBuildingId, plot, 2);
  city.rngSeed = rng.seed;
  city.buildings.push(b);
  city.nextBuildingId += 1;
  city.plots.splice(i, 1);
  city.activeId = b.id; // jump to the new site
  city.rep += SIZE_REP[plot.size];

  pushLog(city, 'good', `🏙️ Claimed ${b.name} in ${b.district}. Reputation ×${(1 + city.rep).toFixed(1)}.`);
  return true;
}
